import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id", "idLabel", "ownerId", "type"],
  _excluded2 = ["data"];
import { registerMutation, registerQuery, useMutation, useQuery, useDataFetchingClient } from 'data-fetching-client';
import { useCurrentObjectType, useCurrentViewId, useEditHash, useClientStorage } from 'crm-index-visualization-core/core/data/VizCoreProvider';
import { ViewAPI } from '../data/viewAPI';
import { useMemo } from 'react';
import { ViewDraft } from 'crm-index-visualization-core/core/model/viewDraft';

// Type for view validation functions

// Register mutation to save a view draft
const saveDraftMutation = registerMutation({
  fieldName: 'vizcoreSaveDraft',
  fetcher: async ({
    hash,
    saveAs,
    clientStorage,
    currentView
  }) => {
    if (!hash && !currentView) {
      throw new Error('No hash or current view');
    }
    if (!currentView) {
      throw new Error('No current view');
    }
    const viewDraft = await ViewDraft.fromClientCache(hash !== null && hash !== void 0 ? hash : '', clientStorage).catch(() => null);

    // If there's no draft, use the current BE view version
    const backendView = viewDraft ? await viewDraft.toBEView(clientStorage, currentView) : currentView;
    if (!saveAs) {
      return ViewAPI.putView(backendView, clientStorage);
    }
    // Handle save-as mode if saveAs options are provided
    const rest = _objectWithoutPropertiesLoose(backendView, _excluded);
    const {
      rename,
      validators = [],
      rewrite = view => view
    } = saveAs;
    // Apply rename if provided
    if (rename) {
      rest.name = typeof rename === 'function' ? rename(backendView.name) : rename;
    }
    // Apply rewrite if provided
    const newView = rewrite(rest);
    // Run validators if provided
    await Promise.all(validators.map(validator => validator(newView)));
    return ViewAPI.postView(newView, clientStorage);
  },
  args: ['hash', 'saveAs', 'clientStorage', 'currentView']
});
export const currentViewQuery = registerQuery({
  fieldName: 'vizcoreCurrentView',
  fetcher: ({
    viewId,
    objectTypeId
  }) => ViewAPI.getView(viewId, objectTypeId),
  args: ['viewId', 'objectTypeId']
});

/**
 * @deprecated Use `useView` instead
 */
export function useCurrentView() {
  return useQuery(currentViewQuery, {
    variables: {
      viewId: useCurrentViewId(),
      objectTypeId: useCurrentObjectType()
    }
  });
}
/**
 * Hook to save a view draft as a view
 * @param options Optional callbacks for success and error handling, validation functions, and save mode
 * @returns A tuple containing the save function and the mutation result
 *
 * @deprecated Use `useMutateView` instead
 */
export function useSaveViewDraft(options = {}) {
  const {
    data
  } = useCurrentView();
  const [editHash, setEditHash] = useEditHash();
  const clientStorage = useClientStorage();
  const client = useDataFetchingClient();
  const [mutate, mutationResult] = useMutation(saveDraftMutation, Object.assign({}, options, {
    onCompleted: options.onSuccess
  }));
  return useMemo(() => {
    /**
     * Save a view draft as a view
     * @param localOptions Optional configuration for save operations including save-as functionality
     * @returns A promise that resolves to the saved view
     *
     * If the view draft cannot be retrieved or doesn't exist:
     * - When saving normally: falls back to using the current view
     * - When saving as: uses the current view as the base for transformations
     */
    function saveViewDraft(localOptions = {}) {
      var _localOptions$saveAs;
      return mutate({
        variables: {
          hash: editHash,
          saveAs: (_localOptions$saveAs = localOptions.saveAs) !== null && _localOptions$saveAs !== void 0 ? _localOptions$saveAs : options.saveAs,
          clientStorage,
          currentView: data === null || data === void 0 ? void 0 : data.vizcoreCurrentView
        }
      }).then(d => {
        setEditHash(null);
        return d;
      }).then(async d => {
        await client.refetchQueries({
          include: ['vizcoreCurrentView', 'vizcoreSegment', 'vizcoreVizConfig']
        });
        return d;
      });
    }
    return [saveViewDraft, mutationResult];
  }, [mutate, mutationResult, data, editHash, clientStorage, setEditHash, client, options.saveAs]);
}

/**
 * Hook to get the current view meta data
 * @returns A tuple containing the current view meta data and the query result
 *
 * @deprecated Use `useViewMeta` instead
 */
export function useCurrentViewMeta() {
  const _useCurrentView = useCurrentView(),
    {
      data
    } = _useCurrentView,
    rest = _objectWithoutPropertiesLoose(_useCurrentView, _excluded2);
  return useMemo(() => {
    const underlyingData = data === null || data === void 0 ? void 0 : data.vizcoreCurrentView;
    return Object.assign({
      data: underlyingData ? {
        name: underlyingData.name,
        id: underlyingData.id,
        idLabel: underlyingData.idLabel
      } : undefined
    }, rest);
  }, [data, rest]);
}